import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../hooks/useApi';
import { useStations } from '../hooks/useStations';
import type { WarehouseStatsResponse } from '@shared/api-responses';

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString();
}

function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
}

type Freshness = 'green' | 'amber' | 'red' | 'gray';

function freshness(lastSubmission: string | null): Freshness {
  if (!lastSubmission) return 'gray';
  const days = daysSince(lastSubmission);
  if (days <= 7) return 'green';
  if (days <= 14) return 'amber';
  return 'red';
}

const FRESHNESS_STYLES: Record<Freshness, { dot: string; label: string; bg: string }> = {
  green: { dot: 'bg-ems-green', label: 'text-ems-green', bg: 'bg-ems-green/10 border-ems-green/20' },
  amber: { dot: 'bg-ems-amber', label: 'text-ems-amber', bg: 'bg-ems-amber/10 border-ems-amber/20' },
  red: { dot: 'bg-ems-red', label: 'text-ems-red', bg: 'bg-ems-red/10 border-ems-red/20' },
  gray: { dot: 'bg-zinc-600', label: 'text-zinc-500', bg: 'bg-zinc-800/50 border-zinc-700/50' },
};

export default function Warehouse() {
  const navigate = useNavigate();
  const { stations, selectStation } = useStations();
  const [data, setData] = useState<WarehouseStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<WarehouseStatsResponse>('/warehouse/stats')
      .then((stats) => {
        if (!cancelled) setData(stats);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load warehouse stats');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStartCount = () => {
    const fromList = stations.find((s) => s.id === 99);
    const warehouse =
      fromList ??
      (data
        ? {
            id: data.station.stationId,
            name: data.station.stationName,
            code: data.station.stationCode,
            is_active: true,
            admin_only: true,
          }
        : null);
    if (warehouse) {
      selectStation(warehouse);
      navigate('/inventory');
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-surface text-white">
        <div className="px-4 py-4 border-b border-border-subtle">
          <h1 className="text-xl font-bold">Warehouse</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Loading warehouse stats...</p>
        </div>
        <div className="px-4 py-8 grid gap-3 md:max-w-3xl md:mx-auto">
          <div className="skeleton h-36 rounded-2xl" />
          <div className="skeleton h-48 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-dvh bg-surface text-white">
        <div className="px-4 py-4 border-b border-border-subtle">
          <h1 className="text-xl font-bold">Warehouse</h1>
        </div>
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <p className="text-zinc-400 font-medium">Failed to load warehouse stats</p>
          <p className="text-zinc-600 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { station, recentSessions } = data;
  const fresh = freshness(station.lastSubmission);
  const styles = FRESHNESS_STYLES[fresh];

  return (
    <div className="min-h-dvh bg-surface text-white pb-20 md:pb-6">
      <div className="px-4 py-4 border-b border-border-subtle flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Warehouse</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Bulk supply totals for {station.stationCode}</p>
        </div>
        <button
          type="button"
          onClick={handleStartCount}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-dcvfd-accent text-white hover:bg-dcvfd-light transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Count
        </button>
      </div>

      <div className="px-4 py-4 md:max-w-3xl md:mx-auto space-y-6">
        {/* Summary card */}
        <section className="rounded-2xl bg-surface-raised border border-border-subtle p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-base font-semibold text-white">{station.stationName}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{station.stationCode}</p>
            </div>
            <div
              className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium border ${styles.bg}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${styles.dot} ${fresh !== 'gray' ? 'pulse-dot' : ''}`} />
              <span className={styles.label}>
                {fresh === 'gray' ? 'Never' : station.lastSubmission ? timeAgo(station.lastSubmission) : 'Never'}
              </span>
            </div>
          </div>

          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold tabular-nums">
                {station.itemsShort > 0 ? (
                  <span className="text-ems-red">{station.itemsShort}</span>
                ) : (
                  <span className="text-ems-green">0</span>
                )}
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">items short</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-medium text-zinc-300 tabular-nums">{station.itemCount}</p>
              <p className="text-xs text-zinc-500">total counted</p>
            </div>
          </div>
        </section>

        {/* Shortages */}
        <section className="rounded-2xl bg-surface-raised border border-border-subtle p-4">
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Shortages</h2>
          {station.shortages.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">No shortages on the latest count.</p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {station.shortages.map((sh) => (
                <li key={`${sh.itemName}-${sh.category}`} className="flex items-center justify-between py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-100 truncate">{sh.itemName}</p>
                    <p className="text-xs text-zinc-500">{sh.category}</p>
                  </div>
                  <div className="text-right tabular-nums shrink-0 ml-3">
                    <p className="text-sm text-ems-red font-semibold">{sh.delta}</p>
                    <p className="text-[10px] text-zinc-500">
                      {sh.actual} / {sh.target}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Recent sessions */}
        <section className="rounded-2xl bg-surface-raised border border-border-subtle p-4">
          <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Recent Counts</h2>
          {recentSessions.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">No warehouse counts yet.</p>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {recentSessions.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-zinc-100">{timeAgo(s.submittedAt)}</p>
                    <p className="text-xs text-zinc-500">{s.submittedBy ?? 'Unknown submitter'}</p>
                  </div>
                  <div className="text-right tabular-nums shrink-0 ml-3">
                    <p className="text-sm text-zinc-300">{s.itemCount} items</p>
                    <p className="text-[10px] text-zinc-500">{s.itemsShort} short</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
